import { Box, Button, Divider, Grid, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { Loader, CheckboxListTable } from '../../../components';
import { IDropdownResponse } from '../../../models';
import { deleteBulkOptionExclusions, getOptionNames } from '../../../fetch';
import { getAdminLookupNeighborhoods, getAdminLookupPlanStylesForSelectedNeighborhoods } from '../../../fetch/lookups';
import { Save } from '@mui/icons-material';
import { alphaSort } from '../../../helpers';

const BulkOptionInclusionsSchema = Yup.object().shape({
    optionCodesString: Yup.string().required('Required')
})

export const BulkOptionInclusionsForm = () => {

    const { enqueueSnackbar } = useSnackbar();
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
    const neighborhoodActiveFilter = true;

    const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<IDropdownResponse[]>([]);
    const [selectedPlanStyles, setSelectedPlanStyles] = useState<IDropdownResponse[]>([]);

    const [optionNames, setOptionNames] = useState<string>('');
    const [optionCodes, setOptionCodes] = useState<string>('');
    const [neighborhoodFilter, setNeighborhoodFilter] = useState<string>('');
    const [filteredNeighborhoods, setFilteredNeighborhoods] = useState<IDropdownResponse[]>([]);

    const { isLoading: isLoadingNeighborhoods, data: neighborhoods } = useQuery<IDropdownResponse[], Error>(
        ['neighborhoods'],
        () => getAdminLookupNeighborhoods(neighborhoodActiveFilter),
        {
            onSuccess: d => {
                alphaSort(d, 'description');
                setFilteredNeighborhoods(d);
            }
        }
    );

    let [isLoadingPlanStyles, setIsLoadingPlanStyles] = useState<boolean>(false);
    let [planStyles, setPlanStyles] = useState<IDropdownResponse[]>([]);

    function getIntValues(dropdownResponses: IDropdownResponse[]) {
        const selectedValues = new Array<number>();

        for (var i = 0; i < dropdownResponses.length; i++) {
            var value = parseInt(dropdownResponses[i].value);
            selectedValues.push(value);
        }

        return selectedValues;
    }

    function getStringValues(dropdownResponses: IDropdownResponse[]) {
        const selectedValues = [];

        for (var i = 0; i < dropdownResponses.length; i++) {
            var value = dropdownResponses[i].value;
            selectedValues.push(value);
        }

        return selectedValues;
    }

    function getOptionCodesArray(optionCodesString: string) {
        const optionCodesArray = new Array<number>();
        const optionCodesSplit = optionCodesString.split(',');

        optionCodesSplit.forEach(value => {
            const optionCodeNumber = Number(value);
            if (optionCodeNumber) {
                optionCodesArray.push(optionCodeNumber);
            }
        });

        return optionCodesArray;
    }



    useEffect(() => {
        if (optionCodes === '') {
            setOptionNames('');
            return;
        }

        const optionCodesArray = getOptionCodesArray(optionCodes);

        if (optionCodesArray.length === 0) {
            setOptionNames('');
            return;
        }

        if (timer) clearTimeout(timer);
        const newTimer = setTimeout(async () => {
            if (optionCodesArray) {
                try {
                    const result = await getOptionNames(optionCodesArray);
                    if (!result) setOptionNames('No Option Name Found');

                    let displayString = '';
                    result.forEach(value => displayString += "\n" + value);

                    setOptionNames(displayString);
                } catch (error) {
                    console.error('Failed to fetch option name:', error);
                }
            }
        }, 2000);
        setTimer(newTimer);

        // Cleanup function to clear the timer
        return () => {
            if (newTimer) clearTimeout(newTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionCodes]);

    useEffect(() => {

        if (!neighborhoodFilter) {
            // If there is no filter specified, we simply revert back to the full list
            setFilteredNeighborhoods(neighborhoods);
            return;
        }

        let listOfFilteredNeighborhoods = new Array<IDropdownResponse>();

        neighborhoods.forEach(value => {
            if (value.description.toLowerCase().includes(neighborhoodFilter.toLowerCase())) {
                listOfFilteredNeighborhoods.push(value);
            }
        });

        setFilteredNeighborhoods(listOfFilteredNeighborhoods);

    }, [neighborhoodFilter, neighborhoods]);

    useEffect(() => {
        
        const fetchPlanStyles = async () => {
            const selectedNeighborhoodIds = getIntValues(selectedNeighborhoods);
            const postData = { neighborhoodIds: selectedNeighborhoodIds };
            setIsLoadingPlanStyles(true);
            setPlanStyles(await getAdminLookupPlanStylesForSelectedNeighborhoods(postData));
            setIsLoadingPlanStyles(false);
        }        
        fetchPlanStyles();
    }, [selectedNeighborhoods]);

    return (
        <Formik
            enableReinitialize={true}
            initialValues={{
                optionCodesString: '',
                optionCodes: [],
                selectedNeighborhoods: [],
                selectedPlanStyles: []
            }}
            validationSchema={BulkOptionInclusionsSchema}
            onSubmit={async (values, actions) => {
                try {
                    values.selectedNeighborhoods = getIntValues(selectedNeighborhoods);
                    values.selectedPlanStyles = getStringValues(selectedPlanStyles);
                    values.optionCodes = getOptionCodesArray(values.optionCodesString);
                    const valuesAdded = await deleteBulkOptionExclusions(values);
                    if (valuesAdded > 0) {
                        enqueueSnackbar(valuesAdded + ` Option Exclusions deleted!`, {
                            variant: 'success'
                        });
                    }
                    else {
                        enqueueSnackbar(`No Neighborhood Series found for the provided selections, no inclusions performed.`, {
                            variant: 'info'
                        });
                    }
                    setOptionNames('');
                    setNeighborhoodFilter('');
                    setSelectedNeighborhoods([]);
                    setSelectedPlanStyles([]);
                    actions.resetForm();
                } catch (error: any) {
                    enqueueSnackbar(error?.Detail ?? 'Error Performing Option Inclusions, please try again.', {
                        variant: 'error'
                    });
                }
            }}
        >
            {({
                isSubmitting,
                values,
                initialValues,
                setFieldValue,
                handleSubmit,
                dirty,
                isValid,
                handleBlur,
                errors,
                touched
            }) => {
                return (
                    <Form onSubmit={handleSubmit}>
                        {isSubmitting && <Loader type="overlay" position="centered" title="Submitting..." />}

                        <Grid container spacing={1}>
                            <Grid item xs={12} lg={8}>
                                <Typography>
                                    This form will remove any option exclusion for the specified option in all Neighborhood Series in the selected Neighborhoods where the
                                    series contains one of the selected Plan Styles.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6} lg={5}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            onChange={e => {
                                                setFieldValue('optionCodesString', e.target.value);
                                                setOptionCodes(e.target.value);
                                                setOptionNames('Searching for option name(s)...');
                                            }}
                                            disabled={isLoadingNeighborhoods || isLoadingPlanStyles}
                                            name={`optionCodesString`}
                                            id={`optionCodesString`}
                                            label="Sage Option Code(s)"
                                            size="small"
                                            value={values.optionCodesString}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.optionCodesString && errors.optionCodesString)}
                                            helperText={(touched.optionCodesString && errors.optionCodesString) || 'To enter more than one option code separate with commas'}
                                        />
                                        <Typography variant="subtitle1" component="span" sx={{ whiteSpace: 'pre-line' }}>
                                            {optionNames}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <h2>Select Neighborhoods</h2>
                                        <TextField
                                            fullWidth
                                            onChange={e => {
                                                setNeighborhoodFilter(e.target.value.trim());
                                            }}
                                            disabled={isLoadingNeighborhoods}
                                            value={neighborhoodFilter}
                                            name={`neighborhoodFilter`}
                                            id={`neighborhoodFilter`}
                                            label="Neighborhood Filter"
                                            size="small"
                                            helperText='(Optional) Enter text to filter the list of neighborhoods'
                                        />
                                        <Divider sx={{ mb: 2 }} />
                                        <Box mb={1}>
                                            {filteredNeighborhoods && filteredNeighborhoods.length !== 0 && (
                                                <CheckboxListTable
                                                    data={filteredNeighborhoods}
                                                    isLoading={isLoadingNeighborhoods}
                                                    selectedItems={selectedNeighborhoods}
                                                    setSelectedItems={setSelectedNeighborhoods}
                                                />
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <h2>Select Plan Styles</h2>
                                        <Box mb={1}>
                                            {planStyles && planStyles.length !== 0 && (
                                                <CheckboxListTable
                                                    data={planStyles}
                                                    isLoading={isLoadingPlanStyles}
                                                    selectedItems={selectedPlanStyles}
                                                    setSelectedItems={setSelectedPlanStyles}
                                                />
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Box mt={1}>
                            <Button
                                disabled={!dirty || isSubmitting || !isValid || isLoadingNeighborhoods || isLoadingPlanStyles}
                                type="submit"
                                startIcon={<Save />}
                                variant="contained"
                                color="primary"
                            >
                                Submit
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};